import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CardActionArea,
  Dialog,
  DialogContent,
  Alert,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import StarIcon from '@mui/icons-material/Star';
import CompressIcon from '@mui/icons-material/Compress';
import VideoCompressor from './VideoCompressor';
import VideoTrimmer from './VideoTrimmer';
import MobileVideoEditor from './MobileVideoEditor';

import DeleteIcon from '@mui/icons-material/Delete';
import AddImageIcon from '../Icons/AddImageIcon';
import { compressImages, CompressionResult } from '../Utils/imageCompression';

interface MediaUploadProps {
  onImagesChange: (images: { url: string; caption: string; featured: boolean; }[]) => void;
  onVideoChange?: (video: { url: string; }[]) => void;
  maxImages?: number;
  defaultAspectRatio?: number;
  value?: { url: string; caption: string; featured: boolean; }[];
  type?: string;
  setType?: (type: string) => void;
  /**
   * Controls what users are allowed to upload.
   *  - "image-only" → user can upload only images
   *  - "image-video" → user can upload images or a single video (default)
   */
  mediaMode?: 'image-only' | 'image-video';
  /**
   * Enable video compression before upload
   */
  enableVideoCompression?: boolean;
  /**
   * Maximum video duration in seconds (for compression)
   */
  maxVideoDuration?: number;
  /**
   * Enable video trimming functionality
   */
  enableVideoTrimming?: boolean;
  /**
   * Default segment duration for trimming (in seconds)
   */
  defaultTrimDuration?: number;
  /**
   * Enable image compression before upload
   * Images larger than 500KB will be compressed
   */
  enableImageCompression?: boolean;
}

interface PreviewImage {
  url: string;
  caption: string;
  featured: boolean;
  file?: File; // Add the file object as optional (or required based on your need)
}

interface PreviewVideo {
  url: string;
  file?: File;
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  onImagesChange,
  onVideoChange,
  maxImages = 5,
  value = [],
  type = '',
  setType,
  mediaMode = 'image-video',
  enableVideoCompression = true,
  maxVideoDuration = 30,
  enableVideoTrimming = false,
  defaultTrimDuration = 30,
  enableImageCompression = true
}) => {

  const [images, setImages] = useState<PreviewImage[]>(value);
  const [videos, setVideos] = useState<PreviewVideo[]>(value);
  const [video, setVideo] = useState<PreviewVideo | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [showCompressor, setShowCompressor] = useState(false);
  const [pendingVideoFile, setPendingVideoFile] = useState<File | null>(null);
  const [compressionStats, setCompressionStats] = useState<{
    originalSize: number;
    compressedSize: number;
    reduction: string;
  } | null>(null);
  const [imageCompressionStats, setImageCompressionStats] = useState<{
    originalSize: number;
    compressedSize: number;
    reduction: string;
    filesCompressed: number;
  } | null>(null);
  const [compressingImages, setCompressingImages] = useState(false);
  const [showTrimmer, setShowTrimmer] = useState(false);
  const [trimmingStats, setTrimmingStats] = useState<{
    originalSize: number;
    trimmedSize: number;
    reduction: string;
  } | null>(null);
  const [showMobileEditor, setShowMobileEditor] = useState(false);

  const [recentImages, setRecentImages] = useState<string[]>([]);
  const [recentVideos, setRecentVideos] = useState<string[]>([]);
  const [fileType, setFileType] = useState<string>('');

  // Derived flag to simplify checks
  const isImageOnly = mediaMode === 'image-only';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileAccept = (isImageOnly || fileType === 'image' || type === 'image')
    ? 'image/*'
    : 'image/*,video/*';


  // Add useEffect to handle value prop changes
  useEffect(() => {
    if (value.length > 0) {
      // setImages(value);
      if (type == 'image') {
        setRecentImages(value.map(img => img.url));
      } else if (type == 'video') {
        setRecentVideos(value.map(video => video.url));
        setVideo(value[0]);
        console.log('recentVideos', recentVideos);
      }
    }
  }, [value]);


  useEffect(() => {
    if (type) {
      setFileType(type);
    }
  }, [type]);


  const handleMediaUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const mediaTypes = new Set(files.map(file => file.type.split('/')[0]));

    // If component is configured for images only, block video uploads early
    if (isImageOnly && mediaTypes.has('video')) {
      alert('Video upload is disabled. Please select images only.');
      event.target.value = '';
      return;
    }

    if (mediaTypes.size > 1) {
      alert('Please select only images or only videos at one time.');
      event.target.value = ''; // Clear input
      return;
    }

    const isImageUpload = mediaTypes.has('image');
    const isVideoUpload = mediaTypes.has('video');

    if (isImageUpload) {
      setFileType('image');
      setType && setType('image');
      await handleImageFiles(files);
    } else if (isVideoUpload) {
      setFileType('video');
      setType && setType('video');
      await handleVideoFiles(files);
    }

    event.target.value = ''; // Reset input for next upload
  };

  const handleImageFiles = async (files: File[]) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (images.length + imageFiles.length > maxImages) {
      alert(`You can only upload up to ${maxImages} images. Currently ${images.length} images.`);
      return;
    }

    // 🔥 Clear video before adding images
    if (video) {
      // URL.revokeObjectURL(video.url);
      setVideo(null);
      onVideoChange && onVideoChange([]);
      setRecentVideos([]);
    }

    let processedFiles = imageFiles;

    // Compress images if enabled (compress only if > 500KB)
    if (enableImageCompression && imageFiles.length > 0) {
      setCompressingImages(true);
      
      try {
        // Simple compression: 500KB threshold for all images
        const compressionOptions = {
          maxSizeBytes: 500 * 1024,      // 500KB threshold
          targetSizeBytes: 400 * 1024,    // Target 400KB after compression
          quality: 0.8,                   // 80% quality
          maxWidth: 1920,                 // Max width 1920px
          maxHeight: 1920,                // Max height 1920px
          mimeType: 'image/jpeg'          // Convert to JPEG
        };
        
        const compressionResults: CompressionResult[] = await compressImages(imageFiles, compressionOptions);
        
        // Calculate total compression stats
        const totalOriginal = compressionResults.reduce((sum, r) => sum + r.originalSize, 0);
        const totalCompressed = compressionResults.reduce((sum, r) => sum + r.compressedSize, 0);
        const filesCompressed = compressionResults.filter(r => r.wasCompressed).length;
        
        if (filesCompressed > 0) {
          const reduction = ((totalOriginal - totalCompressed) / totalOriginal * 100).toFixed(1);
          setImageCompressionStats({
            originalSize: totalOriginal,
            compressedSize: totalCompressed,
            reduction: `${reduction}%`,
            filesCompressed
          });
          console.log(`Compression complete: ${filesCompressed}/${imageFiles.length} files compressed, saved ${reduction}%`);
        }
        
        // Use compressed files
        processedFiles = compressionResults.map(r => r.file);
      } catch (error) {
        console.error('Image compression failed:', error);
        // Fall back to original files if compression fails
        processedFiles = imageFiles;
      } finally {
        setCompressingImages(false);
      }
    }

    const newImages = processedFiles.map((file, index) => {
      const url = URL.createObjectURL(file);
      return {
        url,
        caption: '',
        featured: images.length === 0 && index === 0 && recentImages.length === 0,
        file,
      };
    });

    setRecentImages(prev => [...prev, ...newImages.map(img => img.url)]);
    const updatedImages = [...images, ...newImages];
    setImages(updatedImages);
    onImagesChange(updatedImages);
  };


  const handleVideoFiles = async (files: File[]) => {
    const [file] = files.filter(f => f.type.startsWith('video/'));
    if (!file) return;

    const url = URL.createObjectURL(file);
    const videoEl = document.createElement('video');
    videoEl.preload = 'metadata';

    const isValid = await new Promise<boolean>((resolve) => {
      videoEl.onloadedmetadata = () => {
        // Commented out duration check to allow longer videos
        // resolve(videoEl.duration <= maxVideoDuration);
        resolve(true); // Allow all video durations
      };
      videoEl.src = url;
    });

    // Commented out duration validation alert
    // if (!isValid) {
    //   URL.revokeObjectURL(url);
    //   alert(`"${file.name}" is longer than ${maxVideoDuration} seconds and will be skipped.`);
    //   return;
    // }

    // Check if video compression is enabled and file size is large enough to warrant compression
    const shouldCompress = enableVideoCompression && file.size > 5 * 1024 * 1024; // 5MB threshold

    if (shouldCompress) {
      setPendingVideoFile(file);
      setShowCompressor(true);
      return;
    }

    // Check if video trimming is enabled
    if (enableVideoTrimming) {
      setPendingVideoFile(file);
      setShowTrimmer(true);
      return;
    }

    // Show mobile editor for all videos
    setPendingVideoFile(file);
    setShowMobileEditor(true);
    return;
  };

  const processVideoFile = async (file: File) => {
    const url = URL.createObjectURL(file);

    // Clear images before adding video
    setImages([]);
    onImagesChange([]);
    setRecentImages([]);

    const newVideo: PreviewVideo = { url, file };
    setRecentVideos([url]);
    setVideos([newVideo]);
    setVideo(newVideo);
    onVideoChange && onVideoChange([newVideo]);
  };

  const handleVideoCompressed = (compressedFile: File, originalSize: number, compressedSize: number) => {
    const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
    setCompressionStats({
      originalSize,
      compressedSize,
      reduction: `${reduction}%`
    });

    // Process the compressed file
    processVideoFile(compressedFile);
    setShowCompressor(false);
    setPendingVideoFile(null);
  };

  const handleCompressionCancel = () => {
    setShowCompressor(false);
    setPendingVideoFile(null);
    setCompressionStats(null);
  };

  const handleVideoTrimmed = (trimmedFile: File, originalSize: number, trimmedSize: number) => {
    const reduction = ((originalSize - trimmedSize) / originalSize * 100).toFixed(1);
    setTrimmingStats({
      originalSize,
      trimmedSize,
      reduction: `${reduction}%`
    });

    // Process the trimmed file
    processVideoFile(trimmedFile);
    setShowTrimmer(false);
    setPendingVideoFile(null);
  };

  const handleTrimmingCancel = () => {
    setShowTrimmer(false);
    setPendingVideoFile(null);
    setTrimmingStats(null);
  };

  const handleMobileEditorSave = (editedFile: File, originalSize: number, finalSize: number) => {
    const reduction = ((originalSize - finalSize) / originalSize * 100).toFixed(1);
    setTrimmingStats({
      originalSize,
      trimmedSize: finalSize,
      reduction: `${reduction}%`
    });

    // Process the edited file
    processVideoFile(editedFile);
    setShowMobileEditor(false);
    setPendingVideoFile(null);
  };

  const handleMobileEditorCancel = () => {
    setShowMobileEditor(false);
    setPendingVideoFile(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };






  const handleDeleteImage = (index: number) => {
    setCompressionStats(null);
    setImageCompressionStats(null);
    // Revoke the object URL to prevent memory leaks
    if (images[index]) {
      URL.revokeObjectURL(images[index].url);
    }
    
    // Remove from both recent images and the formatted images array
    setRecentImages(prev => prev.filter((_, i) => i !== index));
    const updatedImages = images.filter((_, i) => i !== index).map((img, i) => ({
      ...img,
      featured: i === 0 // Make sure first image is always featured
    }));
    setImages(updatedImages);
    onImagesChange(updatedImages);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const firstFile = files[0];
    const isVideo = firstFile.type.startsWith('video/');

    if (isVideo) {
      // Clear images if any and set video
      setImages([]);
      onImagesChange([]);

      const blob = new Blob([firstFile], { type: firstFile.type });
      const videoData = {
        url: URL.createObjectURL(blob),
      };
      setVideo(videoData);
      onVideoChange && onVideoChange([videoData]);
    } else {
      // Clear video if any and prepare to add images
      setVideo(null);
      onVideoChange && onVideoChange([]);

      if (files.length + images.length > maxImages) {
        alert(`You can only upload up to ${maxImages} images`);
        return;
      }

      // Store files for processing and start with the first one
      setPendingFiles(files.slice(1));
      const firstImageFile = files[0];

    }
  };


  const removeVideo = (index: number) => {
    setCompressionStats(null);
    // Revoke the object URL to prevent memory leaks
    if (video) {
      URL.revokeObjectURL(video.url);
    }
    // Clear all video-related state
    setVideo(null);
    setVideos([]);
    setRecentVideos([]);
    onVideoChange && onVideoChange([]);
    // Reset file type and type prop
    setFileType('');
    setType && setType('');
  };

  // Add a function to get the upload progress status
  const getUploadStatus = () => {
    return `${images.length}/${maxImages} images uploaded`;
  };
  // Determine whether to show image or video previews.
  // 1. If the parent explicitly passes a `type` prop, respect it.
  // 2. Fall back to the internally‐deduced `fileType` (set after the user selects files).
  // 3. When the component is configured in `image-only` mode we always allow image previews.
  const imageCondition = recentImages.length > 0 && (
    type === 'image' || fileType === 'image' || mediaMode === 'image-only'
  );
  const videoCondition = recentVideos.length > 0 && (
    type === 'video' || fileType === 'video'
  );
  const showAddMoreButton = imageCondition && recentImages.length < maxImages;

  return (
    <>
      <Box>
        <input
          type="file"
          multiple
          accept={fileAccept}
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleMediaUpload}
        />
        {/* Upload Status */}
        {imageCondition && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            {getUploadStatus()}
          </Typography>
        )}

        {/* Compression Stats Alert */}
        {/* {compressionStats && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Video compressed successfully! Size reduced by {compressionStats.reduction}
              ({formatFileSize(compressionStats.originalSize)} → {formatFileSize(compressionStats.compressedSize)})
            </Typography>
          </Alert>
        )} */}

        {/* Trimming Stats Alert */}
        {/* {trimmingStats && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Video trimmed successfully! Size reduced by {trimmingStats.reduction}
              ({formatFileSize(trimmingStats.originalSize)} → {formatFileSize(trimmingStats.trimmedSize)})
            </Typography>
          </Alert>
        )} */}

        {/* Mobile Editor Stats Alert */}
        {/* {trimmingStats && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Video edited successfully! Size reduced by {trimmingStats.reduction}
              ({formatFileSize(trimmingStats.originalSize)} → {formatFileSize(trimmingStats.trimmedSize)})
            </Typography>
          </Alert>
        )} */}


        {/* Image Previews */}
        {imageCondition ? (
          <Box sx={mediaPreviewContainerStyles}>
            {recentImages.map((image, index) => (
              <Box key={index} sx={mediaBoxStyles}>
                <img
                  src={image}
                  alt={`Image ${index + 1}`}
                  style={mediaStyle}
                />
                {/* Overlay Text */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                >
                  {index + 1}/{recentImages.length}
                </Box>
                <IconButton
                  size="small"
                  sx={deleteIconStyle}
                  onClick={() => handleDeleteImage(index)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                {images[index]?.featured && (
                  <IconButton size="small" sx={featuredIconStyle}>
                    <StarIcon fontSize="small" sx={{ color: '#ffd700' }} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        ) : null}
        {showAddMoreButton && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddToPhotosIcon />}
              sx={{ mt: 2, borderColor: '#F57C00', color: '#F57C00' }}
              onClick={() => fileInputRef.current?.click()}
            >
              Add more
            </Button>
          </Box>
        )}

        {/* Video Previews */}
        {videoCondition ? (
          <>
            <Box sx={mediaPreviewContainerStyles}>
              {recentVideos.map((video, index) => (
                <Box key={index} sx={mediaBoxStyles}>
                  <video
                    src={video}
                    controls
                    autoPlay={true}
                    style={mediaStyle}
                  />
                  {/* Overlay Text */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  >
                    {index + 1}/{recentVideos.length}
                  </Box>
                  <IconButton
                    size="small"
                    sx={deleteIconStyle}
                    onClick={() => removeVideo(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </>
        ) : null}

        {/* Upload Placeholder */}
        {recentImages.length === 0 && recentVideos.length === 0 && (
          <Box sx={uploadCardStyles}>
            <CardActionArea
              sx={uploadCardActionStyles}
              onClick={() => fileInputRef.current?.click()}
            >
              <AddImageIcon sx={{ fontSize: 48, color: '#757575' }} />
              <Typography variant="body1" color="textSecondary" mt={1}>
                Tap to add {isImageOnly ? 'image' : 'image or video'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#757575' }} mt={1}>
                You can add upto 5 images
              </Typography>
              {/* {enableVideoCompression && !isImageOnly && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CompressIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                  <Typography variant="caption" sx={{ color: '#4caf50' }}>
                    Smart video compression enabled
                  </Typography>
                </Box>
              )} */}
            </CardActionArea>
          </Box>
        )}
      </Box>

      {/* Video Compression Dialog */}
      <Dialog
        open={showCompressor}
        onClose={handleCompressionCancel}
        // maxWidth="md"
        // fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            overflow: 'hidden',
            borderRadius: 2,
            // m: 2
          }
        }}
      >
        <DialogContent sx={{ p: 0, m: 0, overflow: 'hidden' }}>
          {pendingVideoFile && (
            <VideoCompressor
              file={pendingVideoFile}
              onCompressed={handleVideoCompressed}
              onCancel={handleCompressionCancel}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Video Trimmer Dialog */}
      <Dialog
        open={showTrimmer}
        onClose={handleTrimmingCancel}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {pendingVideoFile && (
            <VideoTrimmer
              file={pendingVideoFile}
              onTrimmed={handleVideoTrimmed}
              onCancel={handleTrimmingCancel}
              maxDuration={maxVideoDuration}
              defaultSegmentDuration={defaultTrimDuration}
              enableAutoCompression={true}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile Video Editor */}
      {showMobileEditor && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          bgcolor: 'background.default'
        }}>
          {pendingVideoFile && (
            <MobileVideoEditor
              file={pendingVideoFile}
              onSave={handleMobileEditorSave}
              onCancel={handleMobileEditorCancel}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default MediaUpload;

const mediaPreviewContainerStyles: React.CSSProperties = {
  display: 'flex',
  overflowX: 'auto',
  gap: 2,
  paddingBottom: 2,
  // @ts-ignore
  '::-webkit-scrollbar': {
    height: '8px',
  },
  // @ts-ignore
  '::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  // @ts-ignore
  '::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
    // @ts-ignore
    '&:hover': {
      // @ts-ignore
      background: '#666',
    },
  },
};

const mediaBoxStyles: React.CSSProperties = {
  position: 'relative',
  minWidth: '350px',
  height: '350px',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
};

const mediaStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

const deleteIconStyle: React.CSSProperties = {
  position: 'absolute',
  top: 8,
  right: 8,
  // @ts-ignore
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  // @ts-ignore
  '&:hover': {
    // @ts-ignore
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
};

const featuredIconStyle: React.CSSProperties = {
  position: 'absolute',
  top: 8,
  left: 8,
  // @ts-ignore
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  // @ts-ignore
  '&:hover': {
    // @ts-ignore
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
};

const uploadCardStyles: React.CSSProperties = {
  borderRadius: 3,
  backgroundColor: '#fff8f5',
  minHeight: 250,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: '2px solid #eedcce',
  cursor: 'pointer',
};

const uploadCardActionStyles: React.CSSProperties = {
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 2,
};
